/**
 * Découpe un texte libre (la description d'une tâche) en morceaux de texte et de liens.
 *
 * Deux formes reconnues, rien d'autre : le lien markdown `[libellé](https://…)` et l'URL nue
 * `http(s)://…`. Seuls `http:` et `https:` deviennent des liens — `javascript:`, `data:` et le
 * reste restent du texte. Le rendu se fait en éléments React, jamais en HTML : aucun texte de
 * la description n'est interprété comme du balisage.
 */

export type Morceau =
  | { kind: 'texte'; text: string }
  | { kind: 'lien'; href: string; label: string };

// 1er groupe + 2e : `[libellé](url)`, l'URL pouvant porter un niveau de parenthèses
// (`…/Nota_(revue)`) ; 3e : URL nue.
const MOTIF =
  /\[([^\]\n]+)\]\((https?:\/\/(?:[^\s()]|\([^\s()]*\))+)\)|(https?:\/\/[^\s<>"]+)/gi;
const PAIRES: Record<string, string> = { ')': '(', ']': '[', '}': '{' };
const compter = (s: string, c: string): number => s.split(c).length - 1;

/** La ponctuation qui suit une URL dans une phrase n'en fait pas partie. */
function rogner(url: string): string {
  let fin = url.length;
  for (;;) {
    const c = url[fin - 1];
    if (c && '.,;:!?\'"'.includes(c)) { fin--; continue; }
    // Une `)` `]` `}` finale n'appartient à l'URL que si elle referme son ouvrante dans l'URL
    // (Wikipédia) ; sinon elle ferme la phrase : `(voir https://a.fr)`, `[https://a.fr]`.
    const ouvrante = c ? PAIRES[c] : undefined;
    if (c && ouvrante) {
      const corps = url.slice(0, fin);
      if (compter(corps, c) > compter(corps, ouvrante)) { fin--; continue; }
    }
    return url.slice(0, fin);
  }
}

function hrefSur(url: string): string | null {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}

type Lien = { start: number; end: number; href: string; label: string };

function trouverLiens(texte: string): Lien[] {
  const liens: Lien[] = [];
  for (const m of texte.matchAll(MOTIF)) {
    const start = m.index;
    const [brut, libelle, urlMd, urlNue] = m;
    if (urlMd !== undefined && libelle !== undefined) {
      const href = hrefSur(urlMd);
      if (href) liens.push({ start, end: start + brut.length, href, label: libelle });
    } else if (urlNue !== undefined) {
      const url = rogner(urlNue);
      const href = hrefSur(url);
      if (href) liens.push({ start, end: start + url.length, href, label: url });
    }
  }
  return liens;
}

/**
 * Le texte où chaque lien est remplacé par des espaces, à longueur égale : le quick-add
 * (`lib/parser`) y cherche `#projet` et les dates sans les trouver dans une URL (`…#ancre`,
 * `…/today/…`), et ses positions restent celles du texte d'origine.
 */
export function masquerLiens(texte: string): string {
  let masque = texte;
  for (const { start, end } of trouverLiens(texte)) {
    masque = masque.slice(0, start) + ' '.repeat(end - start) + masque.slice(end);
  }
  return masque;
}

/**
 * Le texte tel qu'il se lit hors rendu React (toast, confirmation, file hors-ligne, ligne déjà
 * cliquable) : un lien markdown n'y garde que son libellé, une URL nue reste écrite.
 */
export function texteBrut(texte: string): string {
  return linkify(texte)
    .map((m) => (m.kind === 'texte' ? m.text : m.label))
    .join('');
}

export function linkify(texte: string): Morceau[] {
  const morceaux: Morceau[] = [];
  const pousserTexte = (text: string): void => {
    if (text) morceaux.push({ kind: 'texte', text });
  };

  let curseur = 0;
  for (const { start, end, href, label } of trouverLiens(texte)) {
    pousserTexte(texte.slice(curseur, start));
    morceaux.push({ kind: 'lien', href, label });
    curseur = end;
  }
  pousserTexte(texte.slice(curseur));
  return morceaux;
}
